import React, { useState, useEffect, useContext, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { SkeletonLoading, AppContext } from '@nile/components';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { withRouter } from 'react-router-dom';
import { AccountClient, getPersonalization } from '@nile/graphql';
import { GET_NOTIFICATION_TEMPLATE } from '@nile/banyan/src/Graphql/Mutations/templates';
import {
  UPDATE_REPORT,
  GET_REPORT
} from '@nile/reports/src/modules/reports/graphql';
import { getTransformedChartData } from '@nile/reports/src/modules/charts/utils/chart-transformation';
import { PREVIEW_CHART } from '@nile/reports/src/modules/charts/graphql';
import { preFormatFilterObject } from '@nile/reports/src/modules/component/helper';
import { cloneDeep, isEmpty } from 'lodash';
import ReportSheet from '@nile/reports/src/modules/reports/components/subpages/ReportSheet';
import PrintReport from '@nile/reports/src/modules/reports/components/subpages/PrintReport';

const EditReport = (props) => {
  const { match, reportId } = props;

  const tabContext = useContext(TabContext);
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reportConditionRef = useRef(null);

  const getChartOptions = async (id, chart) => {
    let variable = {
      chart: {
        chartId: id
      }
    };
    if (reportConditionRef.current) {
      const { operands, reportCondition } = await preFormatFilterObject({
        entity: chart?.entity,
        filterObj: cloneDeep(reportConditionRef.current)
      });
      variable = {
        ...variable,
        condition:
          (reportCondition &&
            !isEmpty(operands) && {
              joinOperator: 'AND',
              operands
            }) ||
          null
      };
    }
    const resp = await AccountClient.query({
      query: PREVIEW_CHART,
      variables: { PreviewChartInput: variable },
      fetchPolicy: 'network-only'
    })
      .then((res) => {
        if (res?.data?.previewChart) {
          return {
            chartRawData: res?.data?.previewChart,
            chartOption: getTransformedChartData(
              res.data.previewChart,
              chart.metrics?.length,
              chart.groupBy?.length,
              chart.defaultChartType
            )
          };
        }
      })
      .catch((err) => {
        console.log(err);
      });

    return resp;
  };

  const getChartData = (updatedData, chartId) => {
    return updatedData.charts.find((el) => el.chartId === chartId);
  };

  const setupdatedLayout = async (report) => {
    setLoading(true);
    const updatedNewData = cloneDeep(report);
    const updatedLayout = updatedNewData.layout.layout.map(async (item) => {
      const updatedChart = await getChartData(
        updatedNewData,
        item.chart.chartId
      );
      const updatedChartOptions = await getChartOptions(
        item.chart.chartId,
        updatedChart
      );
      return {
        ...item,
        chart: updatedChart,
        chartOptions: updatedChartOptions?.chartOption,
        chartRawData: updatedChartOptions?.chartRawData
      };
    });
    const latestData = await Promise.all(updatedLayout);
    updatedNewData.layout.layout = latestData;

    setReport(updatedNewData);
    setLoading(false);
  };

  const getDataFromPersonalization = (report) => {
    getPersonalization({
      userId: currentUser?.userId,
      category: 'REPORTDETAIL',
      name: 'reportFilter'
    })
      .then((response) => {
        if (response) {
          const res = response?.data?.getPersonalizationByCategoryAndName;
          if (res) {
            reportConditionRef.current = JSON.parse(res.value).reportFilter;
          }
        }
        setupdatedLayout(report);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getUpdatedChartData = (rep, chartId) => {
    return rep.charts.find((el) => el.chartId === chartId);
  };

  const getTemplateForReport = async (id) => {
    const resp = await AccountClient.query({
      query: GET_NOTIFICATION_TEMPLATE,
      variables: {
        notificationTemplateType: 'REPORT',
        templateId: id
      },
      fetchPolicy: 'network-only'
    })
      .then((res) => {
        if (res?.data?.getNotificationTemplate) {
          return res?.data?.getNotificationTemplate;
        }
      })
      .catch((err) => {
        console.log(err);
      });
    return resp;
  };

  const getUpdatedReportDetail = async (rep) => {
    const updatedNewData = cloneDeep(rep);
    const updatedLayout = updatedNewData.layout.layout.map(async (item) => {
      const updatedChart = await getUpdatedChartData(rep, item.chart.chartId);
      return {
        ...item,
        chart: updatedChart
      };
    });
    const latestData = await Promise.all(updatedLayout);
    updatedNewData.layout.layout = latestData;
    updatedNewData.notificationTemplate = await getTemplateForReport(
      updatedNewData?.notificationTemplateId
    );
    setReport(updatedNewData);
  };

  const getReportDetails = (id) => {
    setLoading(true);
    AccountClient.query({
      query: GET_REPORT,
      variables: {
        reportId: id
      },
      fetchPolicy: 'network-only'
    })
      .then((res) => {
        if (res?.data?.getReport) {
          !props.isExported
            ? getUpdatedReportDetail(res.data.getReport)
            : getDataFromPersonalization(res.data.getReport);
          setError(null);
        }
      })
      .catch((err) => {
        console.log(err);
        setError(err);
      })
      .finally(() => setLoading(false));
  };

  const [updateReport] = useMutation(UPDATE_REPORT, {
    client: AccountClient,
    onCompleted({ updateReport }) {
      setReport(updateReport);
      tabContext.updateTab({
        module: 'REPORT_DETAIL',
        data: {
          reportId: updateReport.reportId,
          title: updateReport.name
        }
      });
      // tabContext.closeTab();
    }
  });

  useEffect(() => {
    if (reportId || match.params.pagename === 'detail') {
      getReportDetails(reportId || match.params.id);
    }
  }, [match.params, reportId]);

  if (loading || !report) {
    return (
      <SkeletonLoading
        hideStepNav
        hideTable
        showCardView
        hideSecondaryHeader
        showPlainCardView={false}
        showBox={false}
      />
    );
  }

  if (error) {
    return <div>Error</div>;
  }

  return (
    <div>
      {!props.isExported && (
        <ReportSheet
          primaryAction={updateReport}
          data={report}
          isExport={!!reportId}
        />
      )}
      {props.isExported && <PrintReport data={report} isExport={!!reportId} />}
    </div>
  );
};

export default withRouter(EditReport);
